import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getFullApiUrl } from "../../config";
import {
  extractVol,
  formatDateSlash,
  formatDurationFromSecond,
  getPodcastSession,
  renderCustomContent,
} from "../../utils";
import { useMembership } from "../../hooks/useMembership";
import {
  durationToSeconds,
  toPlayerEpisode,
  usePlayer,
} from "../../player/PlayerContext";
import {
  Badge,
  Button,
  CoverTile,
  Icon,
  PaywallCard,
} from "../../designSystem";
import { LoadingPage } from "../LoadingPage";
import { ErrorPage } from "../ErrorPage";

const EpisodePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const player = usePlayer();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  // 会员态以服务端付费状态为准，本地 session 只用于构造播放地址
  const { isMember } = useMembership();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [episode, setEpisode] = useState(null);

  const { episodeId, podcastSession } = useMemo(() => {
    const id = window.location.pathname.split("/").pop();
    const searchParams = new URLSearchParams(location.search);
    const session = searchParams.get("podcast_session");
    return { episodeId: id, podcastSession: session };
  }, [location.search]);

  useEffect(() => {
    const fetchPodcastsData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          getFullApiUrl(`/api/v1/subscribe/get_episode?id=${episodeId}`)
        );
        setEpisode(response.data.episode);
      } catch (err) {
        console.error("Error fetching episode data:", err);
        setError("单集内容加载失败，请稍后重试。");
      } finally {
        setLoading(false);
      }
    };
    fetchPodcastsData();
  }, [episodeId, podcastSession]);

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  const vol = extractVol(episode.Title);
  const session = podcastSession || getPodcastSession() || "";
  const locked = !!episode.IsPaid && !isMember;
  const isCurrent = player.episode?.id === episode.ID;
  const durationMin = Math.max(
    1,
    Math.round(durationToSeconds(episode.Duration) / 60)
  );

  const handlePlay = () => {
    if (isCurrent) {
      player.toggle();
      return;
    }
    player.play(toPlayerEpisode(episode, session));
  };

  const handleQueue = () => {
    player.queueAdd(toPlayerEpisode(episode, session));
  };

  return (
    <div className="lt-main">
      <button className="lt-back" onClick={() => navigate("/")}>
        <Icon name="arrow_back" size={16} />
        返回节目
      </button>
      <div className="lt-ephead">
        <CoverTile
          vol={vol}
          size={168}
          seed={episode.ID}
          image={`${process.env.PUBLIC_URL}/logo512.png`}
          style={{ borderRadius: "var(--radius-lg)" }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          {vol != null ? (
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--text-sm)",
                color: "var(--accent-strong)",
                fontWeight: 600,
              }}
            >
              Vol.{vol}
            </span>
          ) : null}
          <h1>{episode.Title}</h1>
          <div className="meta">
            <span>{formatDateSlash(episode.PublishedAt)}</span>·
            <span>{durationMin} 分钟</span>
            {episode.IsPaid ? (
              <>
                ·<Badge variant="member" />
              </>
            ) : null}
          </div>
          <div className="actions">
            {locked ? (
              <Button
                icon="lock"
                size="lg"
                onClick={() => navigate("/membership-intro")}
              >
                成为会员收听
              </Button>
            ) : (
              <>
                <Button
                  icon={isCurrent && player.playing ? "pause" : "play_arrow"}
                  size="lg"
                  onClick={handlePlay}
                >
                  {isCurrent
                    ? player.playing
                      ? "暂停"
                      : `继续播放 ${formatDurationFromSecond(player.currentTime)}`
                    : "开始收听"}
                </Button>
                <Button
                  variant="secondary"
                  icon="playlist_add"
                  size="lg"
                  onClick={handleQueue}
                >
                  加入列表
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {locked ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "28px 0 40px" }}
        >
          <PaywallCard
            onJoin={() => navigate("/membership-intro")}
            onLogin={isAuthenticated ? undefined : () => navigate("/login")}
          />
        </div>
      ) : null}

      <div className="lt-prose">{renderCustomContent(episode.Content)}</div>
    </div>
  );
};

export default EpisodePage;
